import './app-background.css';
import app from './firebase/config';
import { initializeAnalytics } from './firebase-analytics';
import React, { useState, useEffect, useRef, } from 'react';
import { AlertCircle, UserPlus, Crown, ShieldOff } from 'lucide-react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { ref, get, onValue, set, push } from 'firebase/database';
import { auth, database } from './firebase/config';
import AuthScreen from './components/Auth/AuthScreen';
import WelcomeScreen from './components/Welcome/WelcomeScreen';
import AppHeader from './components/Layout/AppHeader';
import TabNavigation from './components/Layout/TabNavigation';
import AppFooter from './components/Layout/AppFooter';
import HomeTab from './components/Home/HomeTab';
import SongsTab from './components/Songs/SongsTab';
import IdeasTab from './components/Ideas/IdeasTab';
import GigsTab from './components/Gigs/GigsTab';
import SetlistTab from './components/Setlist/SetlistTab';
import VenuesTab from './components/Venues/VenuesTab';
import DocumentsTab from './components/Documents/DocumentsTab';
import SettingsTab from './components/Settings/SettingsTab';
import RecordButton from './components/VoiceMemo/RecordButton';
import VoiceMemoRecorder from './components/VoiceMemo/VoiceMemoRecorder';

export default function MainApp() {
  // Auth states
  const [user, setUser] = useState(null);
  const [bandId, setBandId] = useState(null);
  const [bandData, setBandData] = useState(null);
  const [ideasCount, setIdeasCount] = useState(0);
  // eslint-disable-next-line no-unused-vars
const [subscription, setSubscription] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  // UI states
  const [activeTab, setActiveTab] = useState('home');
  const [error, setError] = useState('');
  const [showRemovedModal, setShowRemovedModal] = useState(false);
  const [showNewMemberModal, setShowNewMemberModal] = useState(false);
  const [newMemberName, setNewMemberName] = useState('');
  const [showPromotedModal, setShowPromotedModal] = useState(false);
  const [showDemotedModal, setShowDemotedModal] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallButton, setShowInstallButton] = useState(false);
  const [showWelcomeScreen, setShowWelcomeScreen] = useState(false);
  
  // Recording states
  const [showRecorder, setShowRecorder] = useState(false);
  const [songsForRecorder, setSongsForRecorder] = useState([]);

  // Notification states
  const [showNotifications, setShowNotifications] = useState(false);
  const [recentActivity, setRecentActivity] = useState([]);
  const [unreadComments, setUnreadComments] = useState(0);
  const notificationRef = useRef(null);

  // Track if user had band membership (for detecting removal)
  const hadBandMembership = useRef(false);
  const previousMemberCount = useRef(0);
  const previousRole = useRef(null);

// TODO: Enable feature gating when ready for monetization
// const isPremium = subscription?.plan === 'premium' && subscription?.status === 'active';
const isPremium = true; // Temporarily give everyone premium access

  // Generate unique invite code
  const generateInviteCode = () => {
    const adjectives = ['ROCK', 'JAZZ', 'FUNK', 'SOUL', 'BLUE', 'PUNK'];
    const randomAdj = adjectives[Math.floor(Math.random() * adjectives.length)];
    const randomNum = Math.floor(1000 + Math.random() * 9000);
    return `${randomAdj}-${randomNum}`;
  };

  // Create a new band for orphaned user
  const createNewBandForUser = React.useCallback(async (currentUser) => {
    try {
      console.log('Creating new band for orphaned user:', currentUser.email);
      
      const newBandId = push(ref(database, 'bands')).key;
      const newInviteCode = generateInviteCode();
      const displayName = currentUser.displayName || currentUser.email.split('@')[0];
      const defaultBandName = `${displayName}'s Band`;

      // Write bandMembers first
      await set(ref(database, `bandMembers/${currentUser.uid}`), {
        bandId: newBandId
      });

      // Then write band data
      await set(ref(database, `bands/${newBandId}`), {
        name: defaultBandName,
        inviteCode: newInviteCode,
        createdAt: new Date().toISOString(),
        createdBy: currentUser.uid,
        members: {
          [currentUser.uid]: {
            email: currentUser.email,
            displayName: displayName,
            joinedAt: new Date().toISOString(),
            role: 'leader'
          }
        }
      });

      console.log('New band created successfully:', newBandId);
      return newBandId;
    } catch (err) {
      console.error('Failed to create new band:', err);
      throw err;
    }
  }, []);

  // Initialize analytics
  useEffect(() => {
    initializeAnalytics(app);
  }, []);

  // Capture PWA install prompt
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallButton(true);
    };

    window.addEventListener('beforeinstallprompt', handler);

    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setShowInstallButton(false);
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // Check if user has seen welcome screen
  useEffect(() => {
    if (user && bandId) {
      const hasSeenWelcome = localStorage.getItem('tapsody_welcome_seen');
      if (!hasSeenWelcome) {
        // Small delay to let the app load first
        setTimeout(() => {
          setShowWelcomeScreen(true);
        }, 500);
      }
    }
  }, [user, bandId]);

  // Auth listener
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      console.log('Auth state changed:', currentUser ? currentUser.email : 'null');
      setUser(currentUser);
      
      if (currentUser) {
        try {
          console.log('Auth state changed:', currentUser.email);
          
          // Function to fetch band membership with retries
          const fetchBandMembership = async () => {
            const maxRetries = 5;
            const retryDelay = 2000;
            
            for (let i = 0; i <= maxRetries; i++) {
              console.log(`Fetching band membership (attempt ${i + 1}/${maxRetries + 1})...`);
              const memberRef = ref(database, `bandMembers/${currentUser.uid}`);
              const snapshot = await get(memberRef);
              
              if (snapshot.exists()) {
                const userBandId = snapshot.val().bandId;
                console.log('User belongs to band:', userBandId);
                setBandId(userBandId);
                
                // Fetch band data
                console.log('Fetching band data...');
                const bandNameRef = ref(database, `bands/${userBandId}/name`);
                const bandInviteRef = ref(database, `bands/${userBandId}/inviteCode`);
                const bandMembersRef = ref(database, `bands/${userBandId}/members`);
                const bandDocsRef = ref(database, `bands/${userBandId}/bandDocs`);
                
                const [nameSnap, inviteSnap, membersSnap, docsSnap] = await Promise.all([
                  get(bandNameRef),
                  get(bandInviteRef),
                  get(bandMembersRef),
                  get(bandDocsRef)
                ]);
                
                console.log('Band data loaded successfully');
                setBandData({
                  name: nameSnap.val() || 'Band',
                  inviteCode: inviteSnap.val(),
                  members: membersSnap.val() || {},
                  bandDocs: docsSnap.val() || {}
                });
                
                // Load subscription
                const subRef = ref(database, `bands/${userBandId}/subscription`);
                const subSnap = await get(subRef);
                if (subSnap.exists()) {
                  setSubscription(subSnap.val());
                } else {
                  setSubscription({ plan: 'free', status: 'none' });
                }
                return true;
              }
              
              if (i < maxRetries) {
                console.log(`Band membership not found, waiting ${retryDelay/1000} seconds before retry...`);
                await new Promise(resolve => setTimeout(resolve, retryDelay));
              }
            }
            return false;
          };
          
          const success = await fetchBandMembership();
          
          if (!success) {
            console.warn('No band membership found after retries - creating new band');
            try {
              const newBandId = await createNewBandForUser(currentUser);
              
              const bandNameRef = ref(database, `bands/${newBandId}/name`);
              const bandInviteRef = ref(database, `bands/${newBandId}/inviteCode`);
              const bandMembersRef = ref(database, `bands/${newBandId}/members`);
              
              const [nameSnap, inviteSnap, membersSnap] = await Promise.all([
                get(bandNameRef),
                get(bandInviteRef),
                get(bandMembersRef)
              ]);
              
              setBandId(newBandId);
              setBandData({
                name: nameSnap.val() || 'Band',
                inviteCode: inviteSnap.val(),
                members: membersSnap.val() || {},
                bandDocs: {}
              });
              
              console.log('New band created and loaded successfully');
            } catch (createErr) {
              console.error('Failed to create new band:', createErr);
              setError('Failed to create band: ' + createErr.message);
            }
          }
        } catch (err) {
          console.error('Error loading band data:', err);
          setError('Failed to load band data: ' + err.message);
        }
      } else {
        setBandId(null);
        setBandData(null);
      }
      
      setAuthLoading(false);
    });

    return () => unsubscribe();
  }, [createNewBandForUser]);

// Listen to ideas count for free tier limits
  useEffect(() => {
    if (!user || !bandId) {
      setIdeasCount(0);
      return;
    }

    const ideasRef = ref(database, `bands/${bandId}/ideas`);
    const unsubscribe = onValue(ideasRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        // Count only active ideas (not converted)
        const activeCount = Object.values(data).filter(idea => idea.status !== 'converted').length;
        setIdeasCount(activeCount);
      } else {
        setIdeasCount(0);
      }
    });

    return () => unsubscribe();
  }, [user, bandId]);

  // Listen for band member removal while logged in
  useEffect(() => {
    if (!user || !bandId) {
      hadBandMembership.current = false;
      return;
    }

    const memberRef = ref(database, `bandMembers/${user.uid}`);
    
    const unsubscribe = onValue(memberRef, (snapshot) => {
      if (snapshot.exists()) {
        hadBandMembership.current = true;
      } else if (hadBandMembership.current) {
        hadBandMembership.current = false;
        setShowRemovedModal(true);
      }
    });

    return () => unsubscribe();
  }, [user, bandId]);

  // Real-time listener for band data updates
  useEffect(() => {
    if (!user || !bandId) return;

    const bandRef = ref(database, `bands/${bandId}`);
    
    const unsubscribe = onValue(bandRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        setBandData({
          name: data.name || 'Band',
          inviteCode: data.inviteCode,
          members: data.members || {},
          bandDocs: data.bandDocs || {}
        });
      }
    });

    return () => unsubscribe();
  }, [user, bandId]);

  // Listen for new members joining
  useEffect(() => {
    if (!user || !bandId || !bandData?.members) return;

    const currentMemberCount = Object.keys(bandData.members).length;
    const isLeader = bandData.members[user.uid]?.role === 'leader';

    if (previousMemberCount.current > 0 && currentMemberCount > previousMemberCount.current && isLeader) {
      const memberUids = Object.keys(bandData.members);
      const newMember = memberUids[memberUids.length - 1];
      const newMemberData = bandData.members[newMember];
      
      if (newMemberData && newMember !== user.uid) {
        setNewMemberName(newMemberData.displayName || newMemberData.email);
        setShowNewMemberModal(true);
      }
    }

    previousMemberCount.current = currentMemberCount;
  }, [user, bandId, bandData]);

  // Listen for role changes
  useEffect(() => {
    if (!user || !bandId || !bandData?.members) return;

    const currentRole = bandData.members[user.uid]?.role;

    if (previousRole.current && previousRole.current !== currentRole) {
      if (currentRole === 'leader' && previousRole.current === 'member') {
        setShowPromotedModal(true);
      } else if (currentRole === 'member' && previousRole.current === 'leader') {
        setShowDemotedModal(true);
      }
    }

    if (currentRole) {
      previousRole.current = currentRole;
    }
  }, [user, bandId, bandData]);

  // Fetch songs list for the recorder's "Add to Song" feature
  useEffect(() => {
    if (!user || !bandId) {
      setSongsForRecorder([]);
      return;
    }

    const songsRef = ref(database, `bands/${bandId}/songs`);
    
    const unsubscribe = onValue(songsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const songsArray = Object.keys(data).map(key => ({
          id: key,
          name: data[key].name
        })).sort((a, b) => a.name.localeCompare(b.name));
        setSongsForRecorder(songsArray);
      } else {
        setSongsForRecorder([]);
      }
    });

    return () => unsubscribe();
  }, [user, bandId]);

  const handleWelcomeClose = () => {
    setShowWelcomeScreen(false);
    localStorage.setItem('tapsody_welcome_seen', 'true');
  };

  // Fetch recent activity for notifications
  useEffect(() => {
    if (!user || !bandId) {
      setRecentActivity([]);
      setUnreadComments(0);
      return;
    }

    const bandsRef = ref(database, `bands/${bandId}`);
    
    const unsubscribe = onValue(bandsRef, (snapshot) => {
      if (!snapshot.exists()) return;
      
      const bandFullData = snapshot.val();
      const activity = [];
      let unreadCount = 0;
      
      // Songs activity
      const songs = bandFullData.songs || {};
      Object.keys(songs).forEach(songId => {
        const song = songs[songId];
        
        activity.push({
          type: 'song_created',
          timestamp: song.createdAt,
          name: song.name,
          user: song.createdByName || 'Unknown'
        });
        
        if (song.versions) {
          const versions = Object.keys(song.versions);
          versions.forEach(versionId => {
            const version = song.versions[versionId];
            activity.push({
              type: 'version_uploaded',
              timestamp: version.uploadDate,
              name: `${song.name} - Version ${versions.length}`,
              user: version.uploadedByName || 'Unknown'
            });
            
            if (version.comments) {
              Object.keys(version.comments).forEach(commentId => {
                const comment = version.comments[commentId];
                if (comment.unreadBy && comment.unreadBy[user.uid]) {
                  unreadCount++;
                }
              });
            }
          });
        }
      });
      
      // Gigs activity
      const gigs = bandFullData.gigs || {};
      Object.keys(gigs).forEach(gigId => {
        const gig = gigs[gigId];
        activity.push({
          type: 'gig_created',
          timestamp: gig.createdAt,
          name: gig.venue,
          user: gig.createdByName || 'Unknown'
        });
      });
      
      // Sort by timestamp, take top 8
      const sortedActivity = activity
        .filter(item => item.timestamp)
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
        .slice(0, 8);
      
      setRecentActivity(sortedActivity);
      setUnreadComments(unreadCount);
    });

    return () => unsubscribe();
  }, [user, bandId]);

  // Close notification panel on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (notificationRef.current && !notificationRef.current.contains(e.target)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // eslint-disable-next-line no-unused-vars
  const hasNotifications = recentActivity.length > 0;

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setShowInstallButton(false);
    }
    
    setDeferredPrompt(null);
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setActiveTab('home');
      setBandId(null);
      setBandData(null);
      hadBandMembership.current = false;
      previousMemberCount.current = 0;
      previousRole.current = null;
    } catch (error) {
      setError('Failed to log out');
    }
  };

  const handleRemovedAcknowledgment = async () => {
    setShowRemovedModal(false);
    await handleLogout();
  };

  // Show loading spinner during auth check
  if (authLoading) {
    return (
      <div className="min-h-screen app-bg text-white flex items-center justify-center">
        <div className="app-bg-orbs">
          <div className="app-bg-orb app-bg-orb-aqua" />
          <div className="app-bg-orb app-bg-orb-magenta" />
        </div>
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-300">Loading...</p>
        </div>
      </div>
    );
  }

  // Show auth screen if not logged in
  if (!user) {
    return <AuthScreen />;
  }

  // Main app UI (when logged in)
  return (
    <>
    <div className="min-h-screen app-bg text-white p-4 sm:p-6">
      {/* Background orbs */}
      <div className="app-bg-orbs">
        <div className="app-bg-orb app-bg-orb-aqua" />
        <div className="app-bg-orb app-bg-orb-magenta" />
      </div>

      {/* Removal Modal */}
      {showRemovedModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="card-3d border-2 border-brand rounded-card p-6 sm:p-8 max-w-md w-full shadow-2xl">
            <div className="flex items-start gap-4 mb-6">
              <div className="bg-brand/20 p-3 rounded-full">
                <AlertCircle className="w-8 h-8 text-brand" />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold mb-2">Removed from Band</h2>
                <p className="text-slate-300">
                  You have been removed from the band by the band leader. You will be logged out and can log back in to create a new band.
                </p>
              </div>
            </div>
            <button
              onClick={handleRemovedAcknowledgment}
              className="w-full bg-brand hover:bg-brand-hover transition px-6 py-3 font-semibold text-lg rounded-pill"
            >
              Understood
            </button>
          </div>
        </div>
      )}

      {/* New Member Joined Modal */}
      {showNewMemberModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="card-3d border-2 border-green-500 rounded-card p-6 sm:p-8 max-w-md w-full shadow-2xl">
            <div className="flex items-start gap-4 mb-6">
              <div className="bg-green-500/20 p-3 rounded-full">
                <UserPlus className="w-8 h-8 text-green-500" />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold mb-2">New Member Joined</h2>
                <p className="text-slate-300">
                  <span className="font-semibold text-white">{newMemberName}</span> has joined your band!
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowNewMemberModal(false)}
              className="w-full bg-green-500 hover:bg-green-600 transition px-6 py-3 font-semibold text-lg rounded-pill"
            >
              Great!
            </button>
          </div>
        </div>
      )}

      {/* Promoted to Leader Modal */}
      {showPromotedModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="card-3d border-2 border-yellow-500 rounded-card p-6 sm:p-8 max-w-md w-full shadow-2xl">
            <div className="flex items-start gap-4 mb-6">
              <div className="bg-yellow-500/20 p-3 rounded-full">
                <Crown className="w-8 h-8 text-yellow-500" />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold mb-2">You Are Now Band Leader!</h2>
                <p className="text-slate-300">
                  You have been promoted to band leader. You now have full control over band management, member permissions, and settings.
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowPromotedModal(false)}
              className="w-full bg-yellow-500 hover:bg-yellow-600 transition px-6 py-3 font-semibold text-lg text-slate-900 rounded-pill"
            >
              Got It!
            </button>
          </div>
        </div>
      )}

      {/* Demoted to Member Modal */}
      {showDemotedModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="card-3d border-2 border-orange-500 rounded-card p-6 sm:p-8 max-w-md w-full shadow-2xl">
            <div className="flex items-start gap-4 mb-6">
              <div className="bg-orange-500/20 p-3 rounded-full">
                <ShieldOff className="w-8 h-8 text-orange-500" />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold mb-2">You Are No Longer Band Leader</h2>
                <p className="text-slate-300">
                  Your role has been changed to band member. You can still contribute to songs, gigs, and documents, but you no longer have administrative permissions.
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowDemotedModal(false)}
              className="w-full bg-orange-500 hover:bg-orange-600 transition px-6 py-3 font-semibold text-lg rounded-pill"
            >
              Understood
            </button>
          </div>
        </div>
      )}

      <div className="max-w-4xl mx-auto">
        <AppHeader
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          showNotifications={showNotifications}
          setShowNotifications={setShowNotifications}
          notificationRef={notificationRef}
          unreadComments={unreadComments}
          recentActivity={recentActivity}
          onLogout={handleLogout}
        />

        <TabNavigation activeTab={activeTab} setActiveTab={setActiveTab} />

        {/* Error Display */}
        {error && (
  <div className="bg-red-900/50 rounded-card p-4 mb-6 flex items-start gap-3">
    <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
    <div className="flex-1"><p className="text-red-100 text-sm">{error}</p></div>
    <button onClick={() => setError('')} className="text-red-300 hover:text-red-200">×</button>
  </div>
)}

        {/* Tab Content */}
        {activeTab === 'home' && (
          <HomeTab 
            user={user} 
            bandId={bandId} 
            bandData={bandData} 
            setActiveTab={setActiveTab}
            isPremium={isPremium}
          />
        )}
        {activeTab === 'songs' && <SongsTab user={user} bandId={bandId} isPremium={isPremium} />}
        {activeTab === 'ideas' && <IdeasTab user={user} bandId={bandId} isPremium={isPremium} />}
        {activeTab === 'gigs' && <GigsTab user={user} bandId={bandId} isPremium={isPremium} />}
        {activeTab === 'setlist' && <SetlistTab user={user} bandId={bandId} isPremium={isPremium} />}
        {activeTab === 'venues' && <VenuesTab user={user} bandId={bandId} isPremium={isPremium} />}
        {activeTab === 'documents' && <DocumentsTab user={user} bandId={bandId} />}
        {activeTab === 'settings' && <SettingsTab user={user} bandId={bandId} bandData={bandData} onShowWelcome={() => setShowWelcomeScreen(true)} />}

        {/* Floating Record Button */}
        <RecordButton 
          variant="floating" 
          onClick={() => setShowRecorder(true)} 
        />

        <AppFooter 
          showInstallButton={showInstallButton} 
          onInstallClick={handleInstallClick} 
        />
      </div>
    </div>

    {/* Voice Memo Recorder Modal — outside app-bg so z-index works */}
    <VoiceMemoRecorder
      isOpen={showRecorder}
      onClose={() => setShowRecorder(false)}
      user={user}
      bandId={bandId}
      songs={songsForRecorder}
      isPremium={isPremium}
      ideasCount={ideasCount}
    />
      {/* Welcome Screen */}
      {showWelcomeScreen && <WelcomeScreen onClose={handleWelcomeClose} />}
    </>
  );
}
